import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, Users, TrendingUp, AlertCircle, Search, ChevronDown, ChevronUp, Plus, Gift, X } from 'lucide-react';
import { API_URL } from '../config';

const CongesSolde = ({ authUser, authToken, employees, onNavigate }) => {
  const [soldes, setSoldes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterService, setFilterService] = useState('');
  const [sortField, setSortField] = useState('last_name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [expandedEmployee, setExpandedEmployee] = useState(null);
  const [bonusEditing, setBonusEditing] = useState(null); // { employeeId, value }
  const [bonusSaving, setBonusSaving] = useState(false);

  const isAdmin = ['Patron', 'Super Manager'].includes(authUser.role_name);
  const isManager = authUser.role_name === 'Manager';

  const loadSoldes = useCallback(async () => {
    setLoading(true);
    try {
      let url;
      if (isAdmin) {
        url = `${API_URL}/conges-solde`;
      } else {
        url = `${API_URL}/conges-solde/${authUser.id}`;
      }

      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (!res.ok) throw new Error('Erreur chargement');
      const data = await res.json();

      if (isAdmin) {
        setSoldes(data);
      } else {
        setSoldes([data]);
      }
    } catch (err) {
      console.error('Erreur chargement soldes CP:', err);
    } finally {
      setLoading(false);
    }
  }, [authToken, authUser.id, isAdmin]);

  useEffect(() => { loadSoldes(); }, [loadSoldes]);

  // Services uniques pour le filtre
  const services = [...new Set(soldes.map(s => s.service_name).filter(Boolean))].sort();

  // Filtrage et tri
  const filteredSoldes = soldes
    .filter(s => {
      const matchSearch = `${s.first_name} ${s.last_name}`.toLowerCase().includes(searchTerm.toLowerCase());
      const matchService = !filterService || s.service_name === filterService;
      return matchSearch && matchService;
    })
    .sort((a, b) => {
      let valA, valB;
      if (sortField === 'last_name') {
        valA = a.last_name; valB = b.last_name;
      } else if (sortField === 'restant') {
        valA = a.restant; valB = b.restant;
      } else if (sortField === 'anciennete') {
        valA = a.anciennete_annees; valB = b.anciennete_annees;
      } else {
        valA = a[sortField]; valB = b[sortField];
      }
      if (typeof valA === 'string') return sortOrder === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
      return sortOrder === 'asc' ? valA - valB : valB - valA;
    });

  // Statistiques globales
  const stats = {
    totalAcquis: soldes.reduce((sum, s) => sum + s.total_acquis, 0),
    totalPris: soldes.reduce((sum, s) => sum + s.pris, 0),
    totalRestant: soldes.reduce((sum, s) => sum + s.restant, 0),
    alertes: soldes.filter(s => s.restant < 5).length
  };

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const getSoldeColor = (restant) => {
    if (restant < 5) return 'text-red-600 bg-red-50 dark:bg-red-950/40';
    if (restant < 10) return 'text-orange-600 bg-orange-50 dark:bg-orange-950/40';
    return 'text-green-600 bg-green-50 dark:bg-green-950/40';
  };

  const getProgressColor = (pris, total) => {
    const pct = total > 0 ? (pris / total) * 100 : 0;
    if (pct > 80) return 'bg-red-500';
    if (pct > 60) return 'bg-orange-500';
    return 'bg-blue-500';
  };

  const isPatron = authUser.role_name === 'Patron';

  const handleSaveBonus = async (employeeId, value) => {
    setBonusSaving(true);
    try {
      const res = await fetch(`${API_URL}/employees/${employeeId}/bonus-anciennete`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${authToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ bonus: parseInt(value) })
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.error || 'Erreur');
      } else {
        setBonusEditing(null);
        loadSoldes(); // Recharger les données
      }
    } catch (err) {
      console.error('Erreur sauvegarde bonus:', err);
    } finally {
      setBonusSaving(false);
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ChevronDown className="w-3 h-3 opacity-30" />;
    return sortOrder === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 dark:border-blue-500"></div>
      </div>
    );
  }

  // Vue individuelle pour Employé/Manager avec un seul solde
  if (!isAdmin && soldes.length === 1) {
    const s = soldes[0];
    const pctPris = s.total_acquis > 0 ? (s.pris / s.total_acquis) * 100 : 0;

    return (
      <div className="bg-surface rounded-xl shadow-lg p-3 sm:p-6 max-w-2xl mx-auto">
        <div className="flex items-center gap-3 mb-4 sm:mb-6">
          <div className="bg-blue-100 dark:bg-blue-900/40 p-2 sm:p-3 rounded-xl">
            <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-text-primary">Mon Solde de Congés</h2>
            <p className="text-xs sm:text-sm text-text-muted">Période HCR : {s.periode_debut} au {s.periode_fin}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6">
          <div className="bg-blue-50 dark:bg-blue-950/40 rounded-lg p-4 text-center">
            <p className="text-2xl font-bold text-blue-700">{s.total_acquis}</p>
            <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">Jours acquis</p>
          </div>
          <div className="bg-amber-50 dark:bg-amber-950/40 rounded-lg p-4 text-center">
            <p className="text-2xl font-bold text-amber-700">{s.pris}</p>
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">Jours pris</p>
          </div>
          <div className="bg-orange-50 dark:bg-orange-950/40 rounded-lg p-4 text-center">
            <p className="text-2xl font-bold text-orange-700">{s.en_attente}</p>
            <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">En attente</p>
          </div>
          <div className={`rounded-lg p-4 text-center ${getSoldeColor(s.restant)}`}>
            <p className="text-2xl font-bold">{s.restant}</p>
            <p className="text-xs mt-1">Jours restants</p>
          </div>
        </div>

        <div className="mb-4">
          <div className="flex justify-between text-sm mb-1">
            <span className="text-text-secondary">Consommation</span>
            <span className="font-medium">{pctPris.toFixed(0)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className={`h-3 rounded-full transition-all ${getProgressColor(s.pris, s.total_acquis)}`}
              style={{ width: `${Math.min(pctPris, 100)}%` }}
            />
          </div>
        </div>

        <div className="bg-elevated-1 rounded-lg p-4 mt-4">
          <h3 className="font-semibold text-text-secondary mb-2">Détail du calcul</h3>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-text-secondary">Acquisition de base</span>
              <span>{s.acquis} jours</span>
            </div>
            <div className="flex justify-between">
              <span className="text-text-secondary">Bonus ancienneté HCR ({s.anciennete_annees} an{s.anciennete_annees > 1 ? 's' : ''})</span>
              <span>+{s.bonus_anciennete} jour{s.bonus_anciennete > 1 ? 's' : ''}</span>
            </div>
            <div className="flex justify-between font-medium border-t pt-1 mt-1">
              <span>Total acquis</span>
              <span>{s.total_acquis} jours ouvrables</span>
            </div>
          </div>
        </div>

        <p className="text-xs text-text-muted mt-4 text-center">
          Convention HCR — Décompte en jours ouvrables (lundi-samedi)
        </p>
      </div>
    );
  }

  // Vue tableau pour Patron/Super Manager
  return (
    <div className="bg-surface rounded-xl shadow-lg p-3 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 sm:mb-6">
        <div className="flex items-center gap-3">
          <div className="bg-blue-100 dark:bg-blue-900/40 p-2 sm:p-3 rounded-xl">
            <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-text-primary">Solde des Congés Payés</h2>
            <p className="text-xs sm:text-sm text-text-muted">Convention HCR — Période {soldes[0]?.periode_debut || ''} au {soldes[0]?.periode_fin || ''}</p>
          </div>
        </div>
        <div className="flex gap-2">
          {onNavigate && (
            <button
              onClick={() => {
                onNavigate('conges');
                setTimeout(() => window.dispatchEvent(new CustomEvent('openCongesModal')), 150);
              }}
              className="px-3 py-2 min-h-[44px] bg-teal-600 text-white rounded-lg hover:bg-teal-700 text-sm font-medium transition flex items-center gap-1 active:scale-95"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">Demander un congé</span>
              <span className="sm:hidden">Congé</span>
            </button>
          )}
          <button onClick={loadSoldes} className="px-3 py-2 min-h-[44px] bg-blue-50 dark:bg-blue-950/40 text-blue-600 rounded-lg hover:bg-blue-100 dark:bg-blue-900/40 text-sm font-medium transition active:scale-95">
            Actualiser
          </button>
        </div>
      </div>

      {/* Cartes statistiques */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="w-4 h-4 opacity-80" />
            <span className="text-xs opacity-80">Total acquis</span>
          </div>
          <p className="text-xl sm:text-2xl font-bold">{stats.totalAcquis.toFixed(1)}</p>
        </div>
        <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-4 text-white">
          <div className="flex items-center gap-2 mb-1">
            <Calendar className="w-4 h-4 opacity-80" />
            <span className="text-xs opacity-80">Total pris</span>
          </div>
          <p className="text-2xl font-bold">{stats.totalPris}</p>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white">
          <div className="flex items-center gap-2 mb-1">
            <Users className="w-4 h-4 opacity-80" />
            <span className="text-xs opacity-80">Total restant</span>
          </div>
          <p className="text-2xl font-bold">{stats.totalRestant.toFixed(1)}</p>
        </div>
        <div className={`bg-gradient-to-br ${stats.alertes > 0 ? 'from-red-500 to-red-600' : 'from-gray-400 to-gray-500'} rounded-xl p-4 text-white`}>
          <div className="flex items-center gap-2 mb-1">
            <AlertCircle className="w-4 h-4 opacity-80" />
            <span className="text-xs opacity-80">Solde {"<"} 5 jours</span>
          </div>
          <p className="text-2xl font-bold">{stats.alertes}</p>
        </div>
      </div>

      {/* Filtres */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
          <input
            type="text"
            placeholder="Rechercher un employé..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 min-h-[44px] border border-border-subtle rounded-lg text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <select
          value={filterService}
          onChange={e => setFilterService(e.target.value)}
          className="w-full sm:w-auto px-3 py-3 min-h-[44px] border border-border-subtle rounded-lg text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Tous les services</option>
          {services.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {/* Mobile : cards empilées */}
      <div className="sm:hidden space-y-2">
        {filteredSoldes.map(s => {
          const pctPris = s.total_acquis > 0 ? (s.pris / s.total_acquis) * 100 : 0;
          return (
            <div key={s.employee_id}
              className="bg-surface border border-border-subtle rounded-lg p-3 space-y-2 active:scale-[0.98] transition-transform"
              onClick={() => setExpandedEmployee(expandedEmployee === s.employee_id ? null : s.employee_id)}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-text-primary text-sm">{s.first_name} {s.last_name}</p>
                  <p className="text-xs text-text-muted">{s.service_name} · {s.anciennete_annees} an{s.anciennete_annees > 1 ? 's' : ''}</p>
                </div>
                <span className={`px-2 py-1 rounded-lg text-sm font-bold ${getSoldeColor(s.restant)}`}>
                  {s.restant}j
                </span>
              </div>
              <div className="flex gap-2 text-xs text-center">
                <div className="flex-1 bg-blue-50 dark:bg-blue-950/40 rounded p-1.5">
                  <p className="font-bold text-blue-700">{s.total_acquis}</p>
                  <p className="text-blue-600 dark:text-blue-400">Acquis</p>
                </div>
                <div className="flex-1 bg-amber-50 dark:bg-amber-950/40 rounded p-1.5">
                  <p className="font-bold text-amber-700">{s.pris}</p>
                  <p className="text-amber-600 dark:text-amber-400">Pris</p>
                </div>
                <div className="flex-1 bg-orange-50 dark:bg-orange-950/40 rounded p-1.5">
                  <p className="font-bold text-orange-700">{s.en_attente}</p>
                  <p className="text-orange-600 dark:text-orange-400">Attente</p>
                </div>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-1.5">
                <div className={`h-1.5 rounded-full transition-all ${getProgressColor(s.pris, s.total_acquis)}`}
                  style={{ width: `${Math.min(pctPris, 100)}%` }} />
              </div>
              {expandedEmployee === s.employee_id && (
                <div className="text-xs text-text-secondary space-y-1 pt-2 border-t border-border-subtle">
                  <p><strong>Contrat :</strong> {s.heures_hebdomadaires}h/sem</p>
                  <p><strong>Calcul :</strong> {s.acquis} base + {s.bonus_anciennete} bonus = {s.total_acquis}j</p>
                  <p><strong>Période :</strong> {s.periode_debut} au {s.periode_fin}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Desktop : tableau */}
      <div className="overflow-x-auto hidden sm:block">
        <table className="w-full text-xs sm:text-sm">
          <thead>
            <tr className="border-b border-border-subtle">
              <th className="text-left py-3 px-2 cursor-pointer hover:bg-elevated-1" onClick={() => toggleSort('last_name')}>
                <div className="flex items-center gap-1">Employé <SortIcon field="last_name" /></div>
              </th>
              <th className="text-left py-3 px-2">Service</th>
              <th className="text-center py-3 px-2 cursor-pointer hover:bg-elevated-1" onClick={() => toggleSort('anciennete')}>
                <div className="flex items-center justify-center gap-1">Ancienneté <SortIcon field="anciennete" /></div>
              </th>
              <th className="text-center py-3 px-2">Acquis</th>
              <th className="text-center py-3 px-2">Bonus HCR</th>
              <th className="text-center py-3 px-2">Total</th>
              <th className="text-center py-3 px-2">Pris</th>
              <th className="text-center py-3 px-2">En attente</th>
              <th className="text-center py-3 px-2 cursor-pointer hover:bg-elevated-1" onClick={() => toggleSort('restant')}>
                <div className="flex items-center justify-center gap-1">Restant <SortIcon field="restant" /></div>
              </th>
              <th className="text-center py-3 px-2 w-24 md:w-32">Progression</th>
            </tr>
          </thead>
          <tbody>
            {filteredSoldes.map(s => {
              const pctPris = s.total_acquis > 0 ? (s.pris / s.total_acquis) * 100 : 0;
              const isExpanded = expandedEmployee === s.employee_id;

              return (
                <React.Fragment key={s.employee_id}>
                  <tr
                    className="border-b border-border-subtle hover:bg-elevated-1 cursor-pointer"
                    onClick={() => setExpandedEmployee(isExpanded ? null : s.employee_id)}
                  >
                    <td className="py-3 px-2 font-medium">{s.first_name} {s.last_name}</td>
                    <td className="py-3 px-2 text-text-secondary">{s.service_name}</td>
                    <td className="py-3 px-2 text-center text-text-secondary">{s.anciennete_annees} an{s.anciennete_annees > 1 ? 's' : ''}</td>
                    <td className="py-3 px-2 text-center">{s.acquis}</td>
                    <td className="py-3 px-2 text-center">
                      {s.bonus_anciennete > 0 ? (
                        <span className="bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 px-2 py-0.5 rounded-full text-xs font-medium">+{s.bonus_anciennete}</span>
                      ) : s.bonus_max_legal > 0 && isPatron ? (
                        <button
                          onClick={(e) => { e.stopPropagation(); setBonusEditing({ employeeId: s.employee_id, value: 0, max: s.bonus_max_legal, name: `${s.first_name} ${s.last_name}` }); }}
                          className="text-purple-400 hover:text-purple-600 dark:text-purple-400 text-xs underline transition"
                          title={`Max légal : ${s.bonus_max_legal}j`}
                        >
                          <Gift className="w-3.5 h-3.5 inline" /> Accorder
                        </button>
                      ) : (
                        <span className="text-text-muted">—</span>
                      )}
                    </td>
                    <td className="py-3 px-2 text-center font-semibold">{s.total_acquis}</td>
                    <td className="py-3 px-2 text-center">{s.pris}</td>
                    <td className="py-3 px-2 text-center">
                      {s.en_attente > 0 ? (
                        <span className="bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 px-2 py-0.5 rounded-full text-xs font-medium">{s.en_attente}</span>
                      ) : (
                        <span className="text-text-muted">0</span>
                      )}
                    </td>
                    <td className="py-3 px-2 text-center">
                      <span className={`px-2 py-1 rounded-lg text-xs font-bold ${getSoldeColor(s.restant)}`}>
                        {s.restant}
                      </span>
                    </td>
                    <td className="py-3 px-2">
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all ${getProgressColor(s.pris, s.total_acquis)}`}
                          style={{ width: `${Math.min(pctPris, 100)}%` }}
                        />
                      </div>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr>
                      <td colSpan="10" className="bg-elevated-1 px-4 py-3">
                        <div className="text-xs text-text-secondary space-y-1">
                          <p><strong>Date d'embauche :</strong> {s.date_embauche ? new Date(s.date_embauche).toLocaleDateString('fr-FR') : 'Non renseignée'}</p>
                          <p><strong>Contrat :</strong> {s.heures_hebdomadaires}h/semaine</p>
                          <p><strong>Calcul :</strong> {s.acquis} jours (base) + {s.bonus_anciennete} jours (bonus accordé) = {s.total_acquis} jours ouvrables</p>
                          <p><strong>Période de référence :</strong> {s.periode_debut} au {s.periode_fin}</p>
                          {s.bonus_max_legal > 0 && (
                            <p>
                              <strong>Bonus ancienneté HCR :</strong>{' '}
                              {s.bonus_anciennete > 0
                                ? <span className="text-purple-700 dark:text-purple-300">{s.bonus_anciennete} jour(s) accordé(s)</span>
                                : <span className="text-text-muted">Non accordé</span>
                              }
                              {' '}— max légal : {s.bonus_max_legal} jour(s) pour {s.anciennete_annees} an(s) d'ancienneté
                              {isPatron && s.bonus_anciennete < s.bonus_max_legal && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); setBonusEditing({ employeeId: s.employee_id, value: s.bonus_anciennete, max: s.bonus_max_legal, name: `${s.first_name} ${s.last_name}` }); }}
                                  className="ml-2 text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:text-purple-200 underline"
                                >
                                  Modifier
                                </button>
                              )}
                            </p>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {filteredSoldes.length === 0 && (
        <div className="text-center py-8 text-text-muted">
          Aucun employé trouvé
        </div>
      )}

      <p className="text-xs text-text-muted mt-4 text-center">
        Convention HCR — Décompte en jours ouvrables (lundi-samedi) — {filteredSoldes.length} employé{filteredSoldes.length > 1 ? 's' : ''}
      </p>

      {/* Modale bonus ancienneté */}
      {bonusEditing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
          <div className="w-full sm:max-w-lg sm:mx-4 sm:rounded-2xl rounded-t-2xl max-h-[90vh] sm:max-h-[85vh] flex flex-col overflow-hidden bg-surface">
            <div className="sm:hidden flex justify-center pt-2 pb-1"><div className="w-10 h-1 bg-gray-300 rounded-full" /></div>
            <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b bg-surface shrink-0">
              <div>
                <h3 className="text-lg font-bold text-text-primary">Accorder le bonus ancienneté</h3>
                <p className="text-sm text-text-muted">{bonusEditing.name}</p>
              </div>
              <button onClick={() => setBonusEditing(null)} className="p-2 hover:bg-elevated-1 rounded-full transition">
                <X className="w-5 h-5 text-text-muted" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4">
              <div className="bg-purple-50 dark:bg-purple-950/40 border border-purple-200 rounded-lg p-3">
                <p className="text-sm text-purple-800">
                  <strong>Maximum légal HCR :</strong> {bonusEditing.max} jour(s) supplémentaire(s)
                </p>
                <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">
                  Ce bonus s'ajoute aux 2,5 jours ouvrables acquis par mois.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">
                  Jours de bonus à accorder (0 à {bonusEditing.max})
                </label>
                <input
                  type="number"
                  min="0"
                  max={bonusEditing.max}
                  value={bonusEditing.value}
                  onChange={(e) => setBonusEditing({ ...bonusEditing, value: Math.min(parseInt(e.target.value) || 0, bonusEditing.max) })}
                  className="w-full px-4 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] border border-border-strong rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="shrink-0 border-t bg-surface px-4 sm:px-6 py-3 sm:py-4 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pb-safe">
              <button
                onClick={() => setBonusEditing(null)}
                className="w-full sm:w-auto min-h-[44px] px-4 py-3 sm:py-2 rounded-lg font-medium text-center border text-text-secondary hover:bg-elevated-1 transition active:scale-95"
              >
                Annuler
              </button>
              <button
                onClick={() => handleSaveBonus(bonusEditing.employeeId, bonusEditing.value)}
                disabled={bonusSaving}
                className="w-full sm:w-auto min-h-[44px] px-4 py-3 sm:py-2 rounded-lg font-medium text-center bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50 transition active:scale-95"
              >
                {bonusSaving ? 'Enregistrement...' : 'Accorder le bonus'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CongesSolde;
